import axiosInstance from 'src/config/axios.config';
import { isAxiosError } from 'axios';
import { storage } from 'src/utils/storage';
import { API_ENDPOINTS } from 'src/config/api.config';
import { getBranchEmployees } from './api_data';

// ===== BRANCH DASHBOARD INTERFACES =====

export interface BranchDashboardStats {
  totalEmployees: number;
  activeEmployees: number;
  monthlyAttendanceRate: number;
  averageWorkingHours: number;
  todayActiveEmployees: number;
}

export interface BranchAttendanceTrend {
  date: string;
  attendanceCount: number;
}

export interface BranchEmployeeStatusDistribution {
  ACTIVE: number;
  INACTIVE: number;
}

export interface BranchEmployeePerformance {
  employeeId: string;
  employeeName: string;
  totalWorkHours: number;
  attendanceCount: number;
  averageWorkHours: number;
  status: string;
}

export interface BranchTopPerformer {
  employeeId: string;
  employeeName: string;
  averageWorkHours: number;
  totalWorkHours: number;
  attendanceCount: number;
  rank: number;
}

export interface BranchManager {
  id: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    profilePictureUrl?: string;
    roles: string[];
    companyName?: string;
  };
  branch: {
    id: string;
    name: string;
  };
  assignedAt: string;
  isActive: boolean;
  assignedBy?: string;
  notes?: string;
}

// ===== BRANCH DASHBOARD API =====

export const branchDashboardApi = {
  /**
   * Şube dashboard istatistiklerini getirir
   */
  getDashboardStats: async (branchId: string): Promise<BranchDashboardStats> => {
    try {
      const response = await axiosInstance.get<BranchDashboardStats>(
        `${API_ENDPOINTS.BRANCHES}/${branchId}/dashboard/stats`
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 401) {
          storage.remove('token');
          window.location.href = '/sign-in';
          throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        }
        if (error.response?.status === 403) {
          throw new Error('Bu işlem için yetkiniz bulunmuyor.');
        }
        throw new Error(error.response?.data?.message || 'Dashboard istatistikleri alınırken hata oluştu');
      }
      throw new Error('Dashboard istatistikleri alınırken hata oluştu');
    }
  },
  /**
   * Şube dashboard çalışan istatistiklerini getirir
   */
  getBranchEmployeeStats: async (branchId: string): Promise<BranchDashboardStats> => {
    try {
      const response = await axiosInstance.get<BranchDashboardStats>(
        `${API_ENDPOINTS.BRANCHES}/${branchId}/dashboard/employee-stats`
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 401) {
          storage.remove('token');
          window.location.href = '/sign-in';
          throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        }
        if (error.response?.status === 403) {
          throw new Error('Bu işlem için yetkiniz bulunmuyor.');
        }
        throw new Error(error.response?.data?.message || 'Dashboard çalışan istatistikleri alınırken hata oluştu');
      }
      throw new Error('Dashboard çalışan istatistikleri alınırken hata oluştu');
    }
  },
  /**
   * Şube dashboard çalışan performansını getirir
   */
  getBranchMonthlyAttendanceRate: async (branchId: string): Promise<number> => {
    try {
      const response = await axiosInstance.get<number>(
        `${API_ENDPOINTS.BRANCHES}/${branchId}/dashboard/monthly-attendance-rate`
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 401) {
          storage.remove('token');
          window.location.href = '/sign-in';
          throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        }
        if (error.response?.status === 403) {
          throw new Error('Bu işlem için yetkiniz bulunmuyor.');
        }
        throw new Error(error.response?.data?.message || 'Aylık devam oranı alınırken hata oluştu');
      }
      throw new Error('Aylık devam oranı alınırken hata oluştu');
    }
  },

  /**
   * Şube devam trendi (30 gün)
   */
  getBranchAttendanceTrend: async (branchId: string): Promise<BranchAttendanceTrend[]> => {
    try {
      const response = await axiosInstance.get<BranchAttendanceTrend[]>(
        `${API_ENDPOINTS.BRANCHES}/${branchId}/dashboard/attendance-trend`
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 401) {
          storage.remove('token');
          window.location.href = '/sign-in';
          throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        }
        if (error.response?.status === 403) {
          throw new Error('Bu işlem için yetkiniz bulunmuyor.');
        }
        throw new Error(error.response?.data?.message || 'Devam trendi alınırken hata oluştu');
      }
      throw new Error('Devam trendi alınırken hata oluştu');
    }
  },

  /**
   * Şube çalışan durum dağılımı
   */
  
  getBranchEmployeeStatusDistribution: async (branchId: string): Promise<BranchEmployeeStatusDistribution> => {
    try {
      const response = await axiosInstance.get<BranchEmployeeStatusDistribution>(
        `${API_ENDPOINTS.BRANCHES}/${branchId}/dashboard/employee-status-distribution`
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 401) {
          storage.remove('token');
          window.location.href = '/sign-in';
          throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        }
        if (error.response?.status === 403) {
          throw new Error('Bu işlem için yetkiniz bulunmuyor.');
        }
        throw new Error(error.response?.data?.message || 'Çalışan durum dağılımı alınırken hata oluştu');
      }
      throw new Error('Çalışan durum dağılımı alınırken hata oluştu');
    }
  },

  /**
   * Şube çalışan performansı
   */
  getBranchEmployeePerformance: async (branchId: string): Promise<BranchEmployeePerformance[]> => {
    try {
      const response = await axiosInstance.get<BranchEmployeePerformance[]>(
        `${API_ENDPOINTS.BRANCHES}/${branchId}/dashboard/employee-performance`
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 401) {
          storage.remove('token');
          window.location.href = '/sign-in';
          throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        }
        if (error.response?.status === 403) {
          throw new Error('Bu işlem için yetkiniz bulunmuyor.');
        }
        throw new Error(error.response?.data?.message || 'Çalışan performansı alınırken hata oluştu');
      }
      throw new Error('Çalışan performansı alınırken hata oluştu');
    }
  },
  /**
   * Şube devam trendi (30 gün)
   */
  getAttendanceTrend: async (branchId: string): Promise<BranchAttendanceTrend[]> => {
    try {
      const response = await axiosInstance.get<BranchAttendanceTrend[]>(
        `${API_ENDPOINTS.BRANCHES}/${branchId}/dashboard/attendance-trend`
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 401) {
          storage.remove('token');
          window.location.href = '/sign-in';
          throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        }
        if (error.response?.status === 403) {
          throw new Error('Bu işlem için yetkiniz bulunmuyor.');
        }
        throw new Error(error.response?.data?.message || 'Devam trendi alınırken hata oluştu');
      }
      throw new Error('Devam trendi alınırken hata oluştu');
    }
  },

  /**
   * Şube en iyi performans gösteren çalışanlar
   */
  getBranchTopPerformers: async (branchId: string, limit: number = 5): Promise<BranchTopPerformer[]> => {
    try {
      const response = await axiosInstance.get<BranchTopPerformer[]>(
        `${API_ENDPOINTS.BRANCHES}/${branchId}/dashboard/top-performers`,
        { params: { limit } }
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 401) {
          storage.remove('token');
          window.location.href = '/sign-in';
          throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        }
        if (error.response?.status === 403) {
          throw new Error('Bu işlem için yetkiniz bulunmuyor.');
        }
        throw new Error(error.response?.data?.message || 'En iyi performans listesi alınırken hata oluştu');
      }
      throw new Error('En iyi performans listesi alınırken hata oluştu');
    }
  },
  /**
   * Şube yöneticileri
   */
  getBranchManagerStats: async (branchId: string): Promise<BranchManager[]> => {
    try {
      const response = await axiosInstance.get<BranchManager[]>(
        `${API_ENDPOINTS.BRANCHES}/${branchId}/managers`
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 401) {
          storage.remove('token');
          window.location.href = '/sign-in';
          throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        }
        if (error.response?.status === 403) {
          throw new Error('Bu işlem için yetkiniz bulunmuyor.');
        }
        throw new Error(error.response?.data?.message || 'Şube yöneticileri alınırken hata oluştu');
      }
      throw new Error('Şube yöneticileri alınırken hata oluştu');
    }
  },
  /**
   * Şube çalışan durum dağılımı
   */
  getEmployeeStatusDistribution: async (branchId: string): Promise<BranchEmployeeStatusDistribution> => {
    try {
      const response = await axiosInstance.get<BranchEmployeeStatusDistribution>(
        `${API_ENDPOINTS.BRANCHES}/${branchId}/dashboard/employee-status-distribution`
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 401) {
          storage.remove('token');
          window.location.href = '/sign-in';
          throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        }
        if (error.response?.status === 403) {
          throw new Error('Bu işlem için yetkiniz bulunmuyor.');
        }
        throw new Error(error.response?.data?.message || 'Çalışan durum dağılımı alınırken hata oluştu');
      }
      throw new Error('Çalışan durum dağılımı alınırken hata oluştu');
    }
  },

  /**
   * Şube çalışan performansı
   */
  getEmployeePerformance: async (branchId: string): Promise<BranchEmployeePerformance[]> => {
    try {
      const response = await axiosInstance.get<BranchEmployeePerformance[]>(
        `${API_ENDPOINTS.BRANCHES}/${branchId}/dashboard/employee-performance`
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 401) {
          storage.remove('token');
          window.location.href = '/sign-in';
          throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        }
        if (error.response?.status === 403) {
          throw new Error('Bu işlem için yetkiniz bulunmuyor.');
        }
        throw new Error(error.response?.data?.message || 'Çalışan performansı alınırken hata oluştu');
      }
      throw new Error('Çalışan performansı alınırken hata oluştu');
    }
  },

  /**
   * Şube en iyi performans gösteren çalışanlar
   */
  getTopPerformers: async (branchId: string, limit: number = 5): Promise<BranchTopPerformer[]> => {
    try {
      const response = await axiosInstance.get<BranchTopPerformer[]>(
        `${API_ENDPOINTS.BRANCHES}/${branchId}/dashboard/top-performers`,
        { params: { limit } }
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 401) {
          storage.remove('token');
          window.location.href = '/sign-in';
          throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        }
        if (error.response?.status === 403) {
          throw new Error('Bu işlem için yetkiniz bulunmuyor.');
        }
        throw new Error(error.response?.data?.message || 'En iyi performans listesi alınırken hata oluştu');
      }
      throw new Error('En iyi performans listesi alınırken hata oluştu');
    }
  },

  /**
   * Şube yöneticileri
   */
  getBranchManagers: async (branchId: string): Promise<BranchManager[]> => {
    try {
      const response = await axiosInstance.get<BranchManager[]>(
        `${API_ENDPOINTS.BRANCHES}/${branchId}/managers`
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 401) {
          storage.remove('token');
          window.location.href = '/sign-in';
          throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        }
        if (error.response?.status === 403) {
          throw new Error('Bu işlem için yetkiniz bulunmuyor.');
        }
        throw new Error(error.response?.data?.message || 'Şube yöneticileri alınırken hata oluştu');
      }
      throw new Error('Şube yöneticileri alınırken hata oluştu');
    }
  }
};

export default branchDashboardApi; 